import { useTranslation } from 'react-i18next';

import { ApiError, isChartAccessDenied } from '../../api/client.ts';
import { FailureNotice } from '../../components/AsyncState.tsx';
import { Panel } from '../../components/primitives.tsx';
import { chartAccessDenialMessage } from './chartAccess.ts';

/**
 * One failure rendering, shared by every query on the patient-chart surface: the patient fetch
 * itself and every clinical-tab list. `chart_access_denied` gets its own panel with the server's
 * actual reason (`chartAccess.ts`); anything else falls through to `AsyncState.tsx`'s existing
 * `FailureNotice`, which already distinguishes a contract mismatch from a generic failure.
 *
 * A `chart_access_denied` response is not a bug and not "not signed in" — it is `evaluateChartAccess`
 * (`server/src/clinical/access.ts`) correctly refusing one specific patient's record to a viewer who
 * can read charts in general. Folding it into `FailureNotice`'s generic copy would read as a server
 * error rather than the deliberate refusal it is.
 */
export function QueryFailureNotice({
  error,
  onRetry,
}: {
  readonly error: unknown;
  readonly onRetry?: () => void;
}) {
  const { t } = useTranslation('patientChart');
  if (isChartAccessDenied(error)) {
    const reason = error instanceof ApiError ? error.denialReason : undefined;
    return (
      <Panel title={t('queryFailure.accessRefusedTitle')}>
        {/* `chartAccessDenialMessage` (`chartAccess.ts`) is not owned by this translation pass —
            still English-only until that module's own reasons are translated separately. */}
        <p className="m-0 text-sm text-ink">{chartAccessDenialMessage(reason)}</p>
        <p className="m-0 mt-2 text-sm text-ink-muted">{t('queryFailure.accessRefusedNote')}</p>
      </Panel>
    );
  }
  // `exactOptionalPropertyTypes` (`tsconfig.base.json`) treats "prop omitted" and "prop passed as
  // `undefined`" as distinct — matching `api/client.ts`'s own `signal === undefined ? {} : { signal }`
  // idiom rather than forwarding `onRetry` (typed `(() => void) | undefined` here) directly into a
  // prop typed `onRetry?: () => void`.
  return onRetry === undefined ? (
    <FailureNotice error={error} />
  ) : (
    <FailureNotice error={error} onRetry={onRetry} />
  );
}
